import React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  CircleHelp,
  Gauge,
  Home,
  Layers,
  Settings,
  Smartphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface GuideCardProps {
  title: string;
  description: string;
  detail: string;
  icon: React.ElementType;
  href?: string;
}

const GuideCard: React.FC<GuideCardProps> = ({ title, description, detail, icon: Icon, href }) => {
  const navigate = useNavigate();

  const content = (
    <>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
        <Icon className="h-5 w-5 text-[var(--color-primary)]" />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="font-semibold text-[var(--text-primary)]">{title}</h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">{description}</p>
        <p className="mt-2 text-xs leading-relaxed text-[var(--text-tertiary)]">{detail}</p>
      </div>
      {href && <ArrowRight className="h-5 w-5 shrink-0 text-[var(--text-tertiary)]" />}
    </>
  );

  if (!href) {
    return (
      <article className="flex items-start gap-3 rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
        {content}
      </article>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate(href)}
      className="group flex w-full items-start gap-3 rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 text-left transition-colors hover:bg-[var(--gray-50)] sm:p-5"
    >
      {content}
    </button>
  );
};

const SoverdiDocumentationR7: React.FC = () => {
  const navigate = useNavigate();

  const guides: GuideCardProps[] = [
    {
      title: 'Accueil',
      description: 'Lire l’état général de l’exploitation et repérer rapidement une anomalie.',
      detail: 'Les valeurs indisponibles restent affichées « — » ; une donnée absente n’est jamais remplacée par zéro.',
      icon: Home,
      href: '/r6-soverdi-dashboard',
    },
    {
      title: 'Secteurs',
      description: 'Consulter les secteurs, parcelles, états et mesures disponibles.',
      detail: 'Les noms visibles sont des noms métier. Les identifiants techniques restent protégés et ne servent pas à l’exploitation quotidienne.',
      icon: Layers,
      href: '/r6-soverdi-sectors',
    },
    {
      title: 'Pompage',
      description: 'Suivre l’état de communication et les informations disponibles sur le pompage.',
      detail: 'Un relais à OFF ne prouve pas à lui seul que le moteur est arrêté. Les états restent distincts lorsque la preuve terrain manque.',
      icon: Gauge,
      href: '/r6-soverdi-pumping',
    },
    {
      title: 'Équipements',
      description: 'Consulter les équipements réellement affectés à l’exploitation.',
      detail: 'La page présente les données utiles au client sans exposer DevEUI, canaux relais, payloads ou paramètres de commissioning.',
      icon: Smartphone,
      href: '/r6-soverdi-equipment',
    },
    {
      title: 'Alertes',
      description: 'Consulter les événements détectés par la plateforme.',
      detail: 'Une erreur de lecture API est affichée comme indisponibilité ; elle n’est jamais interprétée comme une absence d’alerte.',
      icon: AlertTriangle,
      href: '/r6-soverdi-alerts',
    },
    {
      title: 'Paramètres',
      description: 'Gérer le profil, les noms métier et les préférences d’affichage disponibles.',
      detail: 'Les réglages techniques terrain, TTN et AWS ne sont pas modifiables depuis le compte client.',
      icon: Settings,
      href: '/r6-soverdi-settings',
    },
  ];

  return (
    <div className="space-y-5 pb-24 sm:space-y-6 lg:pb-6" data-testid="client-documentation-r7">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-primary)]">
            <BookOpen className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">Aide</span>
          </div>
          <h1 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">Documentation</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Repères essentiels pour utiliser l’interface client Agri Safe sans exposer la configuration technique de l’installation.
          </p>
        </div>
        <span className="inline-flex w-fit items-center rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
          Guide intégré
        </span>
      </section>

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <CircleHelp className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-primary)]" />
          <div>
            <h2 className="font-semibold text-[var(--text-primary)]">Principe de lecture</h2>
            <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">
              Agri Safe distingue une valeur réellement mesurée, une valeur nulle et une information indisponible. Cette règle s’applique aux tableaux de bord, secteurs, équipements, alertes et pompage.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Utiliser les principales interfaces</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Sélectionnez une rubrique pour ouvrir directement l’interface correspondante.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {guides.map((guide) => (
            <GuideCard key={guide.title} {...guide} />
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold text-[var(--text-primary)]">Besoin d’assistance ?</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              La page Contact regroupe les coordonnées publiques Agri Safe et les moyens de joindre l’équipe.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/r6-soverdi-contact')}
            className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 text-sm font-semibold text-white"
            data-testid="documentation-open-contact"
          >
            Contacter Agri Safe <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default SoverdiDocumentationR7;
